import {
  BeeFontData,
  //app format types
  AppThemeData,
  AppFontsFormData,
  AppTypographyFormData,
  AppButtonFormData,
  AppLayoutFormData,
  //partial types
  GoogleFontOption,
  CustomSelectOption,
  AppFormatBaseTypographyInputOptions,
  //API format types
  APIThemeData,
  APIFormatThemeTypographyData,
  APIFormatThemeButtonData,
  APIFormatThemeLayoutData,
  APIPaddingOptions,
  APILayoutResponsivePadding,
} from "containers/admin/clients/client/theme-page/preview/preview.types";
import { TypekitFont } from "types";
import { GoogleFont } from "containers/admin/clients/client/theme-page/preview/sub-tabs/fonts/google-fonts/google-fonts.types";

import { BEE_CONFIG_WEIGHT_LABELS } from "containers/admin/clients/client/theme-page/preview/sub-tabs/fonts/fonts.constants";

//OTHER HELPERS
//filter falsy appliedToPreview out of fonts data
export const filterGoogleFonts = (fonts: AppFontsFormData) => {
  //we need to send to BE only those fonts that were applied to Review
  const filteredGoogleFonts = fonts?.googleFonts?.filter((font) => font?.appliedToPreview);

  return { ...fonts, googleFonts: filteredGoogleFonts?.length ? filteredGoogleFonts : [] };
};

//filtering isAlertShown out of typography data
export const filterAlertTypography = (typographyData: AppTypographyFormData) => {
  Object.keys(typographyData).forEach((section) => {
    delete typographyData[section].textColor.isAlertShown;
  });

  return typographyData;
};

//filtering isAlertShown out of button data
export const filterAlertButton = (buttonData: AppButtonFormData) => {
  delete buttonData.backgroundColor.name.isAlertShown;

  return buttonData;
};

// Handle Adobe Fonts Project ID formatting
export const formatAdobeFontsProjectId = (
  adobeFontsList: TypekitFont[],
  formProjectId: string,
): string => {
  return adobeFontsList.length === 0 && formProjectId ? "" : formProjectId;
};

const isValidTypekitFont = (
  font: TypekitFont,
): font is TypekitFont & { name: string; slug: string } => {
  return (
    font.name !== "" && font.css_stack !== "" && font.slug !== "" && typeof font.slug === "string"
  );
};

//FONTS DATA
export const getGroupedAdobeFonts = (adobeFontsList: TypekitFont[]) => {
  //group Adobefonts by not unique css_names
  const groupedAdobeFontsList = adobeFontsList.reduce<Record<string, TypekitFont[]>>(
    (acc, font) => {
      const key = font.css_names?.[0]!;

      if (!acc[key]) acc[key] = [];

      acc[key].push(font);

      return acc;
    },
    {},
  );

  // keep only one with n4 (normal 400 weight) in each group
  const filteredFamilies = Object.values(groupedAdobeFontsList).map((group: TypekitFont[]) => {
    // find with "n4" (normal 400 weight)
    const withN4 = group.find((f: TypekitFont) => f.variations && f.variations.includes("n4"));
    return withN4 || group[0]; // if no with "n4", take first
  });

  return filteredFamilies;
};

export const getUrlForGoogleFont = (fontFamily: string, fonts: string[]) => {
  if (fonts.length === 0) return "";

  let basicUrl = `https://fonts.googleapis.com/css2?family=${fontFamily}:ital,wght@`;

  fonts.forEach((font, index) => {
    basicUrl += `0,${font}`;

    if (index < fonts.length - 1) {
      basicUrl += ";";
    }
  });

  basicUrl += "&display=swap";

  return basicUrl;
};

export const getAdobeFontWeights = (variations: string[]) => {
  return variations?.map((variation) => Number(variation[variation.length - 1]) * 100);
};

export const getGoogleFontWeights = (variants: string[]) => {
  return variants?.map((key: string): string | null => {
    if (key === "regular" || key === "italic") return "400";

    return key.substring(0, 3);
  });
};

const generateBeeAdobeFonts = (adobeFontsList: TypekitFont[], adobeFontsProjectId: string) => {
  const groupedAdobeFonts = getGroupedAdobeFonts(adobeFontsList);

  return groupedAdobeFonts.filter(isValidTypekitFont).map((font: TypekitFont) => {
    const weights = getAdobeFontWeights(font.variations as string[]);

    const uniqueWeights = Array.from(new Set(weights));

    const fontWeight: Record<number, string> = {};

    uniqueWeights.forEach((weight) => {
      fontWeight[weight] = BEE_CONFIG_WEIGHT_LABELS[weight];
    });

    return {
      name: font.name,
      fontFamily: font.css_stack?.replace(/^"([^"]+)"(,?)/, "$1$2"),
      value: font.slug,
      url: `https://use.typekit.net/${adobeFontsProjectId}.css`,
      fontWeight,
    };
  });
};

const generateBeeGoogleFonts = (fontList: GoogleFont[], selectedFonts: GoogleFontOption[]) => {
  const appliedThemeFonts = selectedFonts.map((font) => font.value);

  const filteredGoogleFonts = fontList.filter((font: GoogleFont) =>
    appliedThemeFonts.includes(font.family),
  );

  return filteredGoogleFonts.map(({ family, category, variants }: GoogleFont) => {
    let fontFamily = `'${family}', `;

    if (category === "sans-serif") {
      fontFamily += "Helvetica, Arial, sans-serif";
    } else if (category === "serif") {
      fontFamily += "Georgia, Times, serif";
    } else if (category === "monospace") {
      fontFamily += "Courier New, monospace";
    } else {
      fontFamily += "sans-serif";
    }

    const weights = getGoogleFontWeights(variants);

    const uniqueWeights = Array.from(new Set(weights));

    const fontWeight: Record<number, string> = {};

    uniqueWeights.forEach((weight) => {
      fontWeight[Number(weight)] = BEE_CONFIG_WEIGHT_LABELS[Number(weight)];
    });

    const fullFontUrl = getUrlForGoogleFont(family, uniqueWeights as string[]);

    return {
      name: family,
      fontFamily: fontFamily,
      value: family,
      url: fullFontUrl,
      fontWeight,
    };
  });
};

//other helpers
const getOptionValue = (option: CustomSelectOption | GoogleFontOption | string): string =>
  typeof option === "string" ? option : option?.value || "";

const getButtonWidthValue = (buttonWidth: {
  name: CustomSelectOption;
  custom?: string;
}): string => {
  const widthName = buttonWidth?.name?.value;

  switch (widthName) {
    case "auto":
      return "auto";
    case "fill":
      return "100";
    case "fixed":
      return buttonWidth?.custom ?? "50";
    default:
      return "50";
  }
};

const getBaseTypographyData = (data: AppFormatBaseTypographyInputOptions) => ({
  fontFamily: getOptionValue(data.fontFamily),
  fallbackFontFamily: getOptionValue(data.fallbackFontFamily),
  fontSize: getOptionValue(data.fontSize),
  lineHeight: getOptionValue(data.lineHeight),
  fontWeight: getOptionValue(data.fontWeight),
  textColor: data.customColor || getOptionValue(data.textColor),
});

const getBasePaddingData = (padding: {
  left: string;
  right: string;
  top: string;
  bottom: string;
  isCustom: boolean;
}): APIPaddingOptions => ({
  left: padding.left,
  right: padding.right,
  top: padding.top,
  bottom: padding.bottom,
});

const getResponsivePaddingData = (responsivePadding: {
  mobile: { left: string; right: string; top: string; bottom: string; isCustom: boolean };
  desktop: { left: string; right: string; top: string; bottom: string; isCustom: boolean };
}): APILayoutResponsivePadding => ({
  mobile: getBasePaddingData(responsivePadding.mobile),
  desktop: getBasePaddingData(responsivePadding.desktop),
});

export const transformTypographyData = (
  typography: AppTypographyFormData,
): APIFormatThemeTypographyData => {
  const typographyData: APIFormatThemeTypographyData = {
    heading: {
      ...getBaseTypographyData(typography.heading),
      alignment: typography.heading.alignment,
    },
    subHeading: {
      ...getBaseTypographyData(typography.subHeading),
      alignment: typography.subHeading.alignment,
    },
    paragraph: {
      ...getBaseTypographyData(typography.paragraph),
      alignment: typography.paragraph.alignment,
      linkColor:
        typography.paragraph.customLinkColor || getOptionValue(typography.paragraph.linkColor),
    },
    buttonLabel: getBaseTypographyData(typography.buttonLabel),
  };

  return typographyData;
};

//BUTTON DATA
export const transformButtonData = (buttonData: AppButtonFormData): APIFormatThemeButtonData => {
  const radius = buttonData.borderRadius;
  const radiusName = radius ? getOptionValue(radius.name) : "";

  const bgColor = buttonData.backgroundColor;

  const borderStyle = buttonData.borderStyle;

  return {
    padding: getBasePaddingData(buttonData.padding),
    width: getButtonWidthValue(buttonData.width),
    alignment: buttonData.alignment,
    backgroundColor: (bgColor.name.value === "custom" && bgColor.custom) || bgColor.name.value,
    // borderRadius: (radius.name.value === "custom" && radius.custom) || radiusName,
    border: {
      radius: (radius.name.value === "custom" && radius.custom) || radiusName,
      width: borderStyle.width,
      color:
        (borderStyle.color.value === "custom" && borderStyle.custom) || borderStyle.color.value,
      style: getOptionValue(borderStyle.name),
    },
  };
};

//LAYOUT DATA
export const transformLayoutData = (layoutData: AppLayoutFormData): APIFormatThemeLayoutData => {
  return {
    contentArea: getBasePaddingData(layoutData.contentArea),
    contentBlock: {
      group: layoutData.contentBlock.group.value,
      allBlocks: getResponsivePaddingData(layoutData.contentBlock.allBlocks),
      headings: getResponsivePaddingData(layoutData.contentBlock.headings),
      paragraph: getResponsivePaddingData(layoutData.contentBlock.paragraph),
      button: getResponsivePaddingData(layoutData.contentBlock.button),
    },
  };
};

//transform data to API format theme data
export const transformThemeDataOnSubmit = (
  values: AppThemeData,
  adobeFontsList: TypekitFont[],
  googleFontsList: GoogleFont[],
): APIThemeData => {
  //generate Bee fonts
  const beeAdobeFonts = generateBeeAdobeFonts(adobeFontsList, values.fonts.adobeFontsProjectId);
  const beeGoogleFonts = generateBeeGoogleFonts(googleFontsList, values.fonts.googleFonts);

  //format data before sending
  const filteredFonts = filterGoogleFonts(values.fonts);
  const filteredTypography = filterAlertTypography(values.typography);
  const filteredButton = filterAlertButton(values.button);

  const colors = {
    primary: values.colors.primary || "",
    secondary: values.colors.secondary || "",
    tertiary: values.colors.tertiary || "",
    intense: values.colors.intense,
    bold: values.colors.bold,
    soft: values.colors.soft,
    subtle: values.colors.subtle,
    white: values.colors.white,
  };

  const fonts = {
    googleFonts: filteredFonts.googleFonts.map(getOptionValue),
    adobeFontsProjectId: formatAdobeFontsProjectId(
      adobeFontsList,
      filteredFonts.adobeFontsProjectId,
    ),
    beeFonts: [...beeAdobeFonts, ...beeGoogleFonts] as BeeFontData[],
  };

  const typography = transformTypographyData(filteredTypography);

  const button = transformButtonData(filteredButton);

  const banner = values.banner;

  const layout = transformLayoutData(values.layout);

  return { colors, fonts, typography, button, banner, layout };
};
